// The plain placement ring (2026-09-18, ?ringPlace).
//
// While aiming in ring mode this is the ONLY thing on screen — no
// preview GLB. The user puts it around the rim of their real wheel and
// taps; the wheel is then dropped centred on the ring.
//
// Why that is worth trying: today's drop derives its own reference. It
// resolves a depth or plane hit, measures the template's contact patch
// (or falls back to faceRadiusM x scale.y when the measurement is
// implausible), and lifts by TIRE_CLEARANCE_M so the tire rests on an
// estimated ground line. Every link in that chain is an estimate, and
// the 2026-09-04 change to deriveWheelDimensions moved one of them. A
// ring the user aims by hand replaces the whole chain with a reference
// they can see and correct before committing.
//
// NOT buildSelectionRing (src/ar/selection-ring.ts), deliberately. That
// one is DASHED — the gaps are what make it read as "this wheel is
// selected" — and it throws on a degenerate radius because a placed
// wheel always has one. This is a plain continuous circle used as an
// aiming target before any wheel exists, so it means something
// different, looks different, and must tolerate a template it cannot
// measure. Sharing an implementation would force one shape to carry
// both meanings.

import * as THREE from 'three'

export const PLACEMENT_RING_NAME = 'placement-ring'

/** Ring line thickness as a fraction of the face radius. Thin enough to
 *  aim with, thick enough to stay visible against a bright forecourt. */
const PLACEMENT_RING_THICKNESS_RATIO = 0.035

/** Smooth enough that the circle does not read as a polygon at arm's
 *  length; cheap enough to rebuild per wheel swap. */
const PLACEMENT_RING_SEGMENTS = 96

const PLACEMENT_RING_COLOR_HEX = 0x4ade80

/** Drawn over the camera feed and over any placed wheel — the aiming
 *  target is useless if a wheel already in the scene occludes it. */
const PLACEMENT_RING_RENDER_ORDER = 9_000

/**
 * A plain circle of `faceRadiusM` radius, lying in the FACE plane
 * (local X/Y, flat on Z) and centred on its own origin — so the group's
 * origin IS the rim centre, which is what the drop references.
 *
 * A degenerate radius yields an empty group rather than throwing: it
 * means the template could not be measured, and an unmeasurable wheel
 * should not take the aiming flow down with it.
 */
export function buildPlacementRing(faceRadiusM: number): THREE.Group {
  const group = new THREE.Group()
  group.name = PLACEMENT_RING_NAME
  group.renderOrder = PLACEMENT_RING_RENDER_ORDER
  if (!Number.isFinite(faceRadiusM) || faceRadiusM <= 0) return group

  const outer = faceRadiusM
  const inner = outer * (1 - PLACEMENT_RING_THICKNESS_RATIO)
  const geometry = new THREE.RingGeometry(
    inner,
    outer,
    PLACEMENT_RING_SEGMENTS,
  )
  const material = new THREE.MeshBasicMaterial({
    color: new THREE.Color(PLACEMENT_RING_COLOR_HEX),
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.95,
    // Always on top — see the render-order note above.
    depthTest: false,
    depthWrite: false,
  })
  const circle = new THREE.Mesh(geometry, material)
  circle.renderOrder = PLACEMENT_RING_RENDER_ORDER
  group.add(circle)
  return group
}
